import { useEffect, useState, useMemo } from 'react';
import {
  BarChart3,
  BookOpen,
  Calendar,
  ChevronDown,
  Download,
  FileText,
  FlaskConical,
  Grid3X3,
  Languages,
  Laptop,
  MessageSquare,
  Quote,
  Star,
  Trophy,
  UserRound,
} from 'lucide-react';

import { AwaitingResultsList } from '@/components/marks/AwaitingResultsList';
import { UpcomingExamsSchedule } from '@/components/marks/UpcomingExamsSchedule';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { examCategoryOf, type ExamCategory } from '@/constants/examCategories';
import {
  GRADE_BANDS,
  GRADE_TONE_CLASSES,
  gradeFor,
  gradeForPaper,
  gradeToneClasses,
  type GradeBand,
} from '@/constants/grading';
import { toast } from '@/hooks/useToast';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { StudentMarksList } from '@/types/student';

/**
 * What the printed report card carries at its head.
 *
 * Its own shape rather than either panel's profile: the student panel reads it
 * from `/student/me` and the parent panel from the child's results response,
 * and the card needs the same seven facts from both.
 */
export interface ReportCardHeader {
  /** The school's own name — never hard-coded (CLAUDE.md §4). */
  instituteName: string | null;
  academicYearName: string | null;
  fullName: string;
  admissionNumber: string;
  rollNumber: string | null;
  classDisplayName: string | null;
  /** Null while the office has not named one for the section. */
  classTeacher: string | null;
}

interface ExamMarksReportProps {
  card: ReportCardHeader | null;
  list: StudentMarksList | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  /**
   * Whether the table carries a percentage column.
   *
   * The raw score is never hidden — this only drops the derived figure, for a
   * student who chose on their Settings tab to read "38 of 50" rather than
   * chase a number they are compared on. A prop rather than a read of the
   * student panel's own store, so the parent panel can draw the same card.
   */
  showPercentage?: boolean;
}

// Subject Icon & Badge helper
function getSubjectTheme(subject: string) {
  const norm = subject.toLowerCase();
  if (norm.includes('math')) {
    return {
      icon: <Grid3X3 className="h-4 w-4" />,
      boxBg: 'bg-indigo-50 text-indigo-600 border border-indigo-200 dark:bg-indigo-950/40 dark:border-indigo-800/60 dark:text-indigo-400',
      barColor: 'bg-[#4f46e5]',
    };
  }
  if (norm.includes('sci') || norm.includes('phy') || norm.includes('chem') || norm.includes('bio')) {
    return {
      icon: <FlaskConical className="h-4 w-4" />,
      boxBg: 'bg-emerald-50 text-emerald-600 border border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800/60 dark:text-emerald-400',
      barColor: 'bg-[#059669]',
    };
  }
  if (norm.includes('soc') || norm.includes('hist') || norm.includes('geo') || norm.includes('civic')) {
    return {
      icon: <BookOpen className="h-4 w-4" />,
      boxBg: 'bg-amber-50 text-amber-600 border border-amber-200 dark:bg-amber-950/40 dark:border-amber-800/60 dark:text-amber-400',
      barColor: 'bg-[#d97706]',
    };
  }
  if (norm.includes('telugu') || norm.includes('hindi') || norm.includes('lang') || norm.includes('tamil') || norm.includes('sanskrit')) {
    return {
      icon: <Languages className="h-4 w-4" />,
      boxBg: 'bg-pink-50 text-pink-600 border border-pink-200 dark:bg-pink-950/40 dark:border-pink-800/60 dark:text-pink-400',
      barColor: 'bg-[#db2777]',
    };
  }
  if (norm.includes('comp') || norm.includes('it') || norm.includes('ai') || norm.includes('tech')) {
    return {
      icon: <Laptop className="h-4 w-4" />,
      boxBg: 'bg-sky-50 text-sky-600 border border-sky-200 dark:bg-sky-950/40 dark:border-sky-800/60 dark:text-sky-400',
      barColor: 'bg-[#0284c7]',
    };
  }
  // English / Default
  return {
    icon: <BookOpen className="h-4 w-4" />,
    boxBg: 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-950/40 dark:border-blue-800/60 dark:text-blue-400',
    barColor: 'bg-[#2563eb]',
  };
}

type ExamCategoryTab = 'Term Exams' | 'Unit Tests' | 'Assignments' | 'Overall Report' | 'Upcoming Exams';
type CategoryTab = Extract<ExamCategoryTab, 'Term Exams' | 'Unit Tests' | 'Assignments'>;

/**
 * The badge for a band, or a neutral one beside a dash when there is no grade —
 * from the shared tone map, so a passing "D" can never wear the failing colour
 * here and another one on the family screens.
 */
function gradeBadgeClass(band: GradeBand | null): string {
  return cn('border', gradeToneClasses(band).badge);
}

/** Which group each category tab shows — by exam type alone, the same map the profile reads. */
const CATEGORY_OF_TAB: Record<CategoryTab, ExamCategory> = {
  'Term Exams': 'termExams',
  'Unit Tests': 'unitTests',
  Assignments: 'assignments',
};

function isCategoryTab(tab: ExamCategoryTab): tab is CategoryTab {
  return tab in CATEGORY_OF_TAB;
}

function isInTab(examType: string, tab: CategoryTab): boolean {
  return examCategoryOf(examType) === CATEGORY_OF_TAB[tab];
}

export function ExamMarksReport({
  card,
  list,
  isLoading,
  error,
  onRetry,
  showPercentage: showMarksPercentage = true,
}: ExamMarksReportProps) {
  const marksList = useMemo(() => list?.exams ?? [], [list]);
  const [activeCategoryTab, setActiveCategoryTab] = useState<ExamCategoryTab>('Term Exams');
  const upcoming = useMemo(() => list?.upcoming ?? [], [list]);

  /*
    Nothing marked yet but papers on the timetable: open on those. A child at
    the start of the year would otherwise land on an empty report and never
    see the dates their teachers have already published.
  */
  useEffect(() => {
    if (list && list.exams.length === 0 && list.upcoming.length > 0) {
      setActiveCategoryTab('Upcoming Exams');
    }
  }, [list]);

  /*
    By the exam type the office set and nothing else. Guessing from the name as
    well put "Monthly Test" in two tabs at once and disagreed with the profile.
  */
  const examsByCategory = useMemo(
    () => ({
      'Term Exams': marksList.filter((m) => isInTab(m.examType, 'Term Exams')),
      'Unit Tests': marksList.filter((m) => isInTab(m.examType, 'Unit Tests')),
      Assignments: marksList.filter((m) => isInTab(m.examType, 'Assignments')),
    }),
    [marksList],
  );

  // Current subset of exams for the selected category tab
  const categoryExams = useMemo(() => {
    if (activeCategoryTab === 'Overall Report' || activeCategoryTab === 'Upcoming Exams') return marksList;
    // No fallback to every paper: an empty Unit Tests tab showing the term
    // exams' marks reads as the unit test having gone missing.
    return examsByCategory[activeCategoryTab];
  }, [activeCategoryTab, examsByCategory, marksList]);

  /**
   * Papers of this tab's type that are set but not marked yet. Only marked
   * papers are scorecard lines, so without these a unit test the school has
   * just put on the timetable would appear under Upcoming Exams and nowhere here.
   */
  const categoryUpcoming = useMemo(
    () =>
      isCategoryTab(activeCategoryTab)
        ? upcoming.filter((paper) => isInTab(paper.examType, activeCategoryTab))
        : [],
    [activeCategoryTab, upcoming],
  );
  const isCategoryUnmarked =
    isCategoryTab(activeCategoryTab) && Boolean(list) && categoryExams.length === 0;

  /**
   * Papers already sat that carry no mark yet — this tab's type, or all of them
   * on the Overall Report. They are neither scorecard lines nor upcoming, so
   * without this a paper vanished from the day after it was sat until marked.
   */
  const categoryAwaiting = useMemo(() => {
    const awaiting = list?.awaiting ?? [];
    if (activeCategoryTab === 'Overall Report') return awaiting;
    return isCategoryTab(activeCategoryTab)
      ? awaiting.filter((paper) => isInTab(paper.examType, activeCategoryTab))
      : [];
  }, [activeCategoryTab, list]);

  // Unique exam names for the active category
  const examTermsList = useMemo(() => {
    return [...new Set(categoryExams.map((mark) => mark.name))];
  }, [categoryExams]);

  // Selected exam dropdown
  const [selectedExamTest, setSelectedExamTest] = useState<string>('');

  /*
    The chosen exam, else the first in this tab. Null when nothing is marked at
    all — the screen then says so instead of titling itself after an exam that
    does not exist.
  */
  const activeExamName = useMemo(() => {
    if (selectedExamTest && examTermsList.includes(selectedExamTest)) {
      return selectedExamTest;
    }
    return examTermsList[0] ?? marksList[0]?.name ?? null;
  }, [selectedExamTest, examTermsList, marksList]);
  const activeExamTitle = activeExamName ?? 'No exam marked yet';

  const filteredExams = useMemo(() => {
    if (activeExamName === null) return [];
    return marksList.filter((m) => m.name === activeExamName);
  }, [marksList, activeExamName]);

  // Chronological list of all unique exams for historical comparison
  const chronologicalExams = useMemo(() => {
    const allUniqueNames = [...new Set(marksList.map((m) => m.name))];
    return allUniqueNames
      .map((name) => {
        const papers = marksList.filter((m) => m.name === name);
        const firstDate = papers.map((p) => p.examDate).filter(Boolean).sort()[0] || '';
        const satPapers = papers.filter((m) => !m.isAbsent && m.marksObtained !== null);
        const totalObt = satPapers.reduce((acc, curr) => acc + (curr.marksObtained ?? 0), 0);
        const totalM = satPapers.reduce((acc, curr) => acc + curr.maxMarks, 0);
        // Null, not 0, when nothing in that exam was scored — a missed exam is
        // not one the child scored nothing in.
        const pct = totalM > 0 ? Math.round((totalObt / totalM) * 100) : null;
        return {
          name,
          firstDate,
          pct,
        };
      })
      .sort((a, b) => a.firstDate.localeCompare(b.firstDate));
  }, [marksList]);

  const currentExamIndex = useMemo(() => {
    return chronologicalExams.findIndex((e) => e.name === activeExamName);
  }, [chronologicalExams, activeExamName]);

  const prevExam = useMemo(() => {
    if (currentExamIndex > 0) {
      return chronologicalExams[currentExamIndex - 1];
    }
    return null;
  }, [chronologicalExams, currentExamIndex]);

  // Current Exam Aggregates
  const sat = filteredExams.filter((m) => !m.isAbsent && m.marksObtained !== null);
  const totalObtained = sat.reduce((acc, curr) => acc + (curr.marksObtained ?? 0), 0);
  const totalMax = sat.reduce((acc, curr) => acc + curr.maxMarks, 0);
  /*
    Null when no paper in this exam carries a score — absent from all of them,
    or none marked yet. Printing 0% there would tell a family their child
    scored nothing, which is a different fact from not having sat.
  */
  const currentPctNum = totalMax > 0 ? Math.round((totalObtained / totalMax) * 100) : null;
  const overallPercentage = currentPctNum === null ? '—' : `${currentPctNum}%`;
  const totalObtainedText = sat.length > 0 ? String(totalObtained) : '—';
  const overallBand = gradeFor(totalObtained, totalMax);

  // Dynamic Percentage Comparison Trend
  const percentageTrend = useMemo(() => {
    if (currentPctNum === null) return { text: 'No paper scored yet', isPositive: null };
    if (!prevExam) return { text: 'No earlier exam to compare', isPositive: null };
    if (prevExam.pct === null) {
      return { text: `Nothing scored in ${prevExam.name} to compare`, isPositive: null };
    }
    const diff = currentPctNum - prevExam.pct;
    if (diff > 0) return { text: `↑ ${diff}% from ${prevExam.name}`, isPositive: true };
    if (diff < 0) return { text: `↓ ${Math.abs(diff)}% from ${prevExam.name}`, isPositive: false };
    return { text: `→ Same as ${prevExam.name}`, isPositive: null };
  }, [prevExam, currentPctNum]);

  // Dynamic Class Rank & Rank Trend
  const rankData = activeExamName === null ? undefined : list?.ranks?.[activeExamName];
  const currentRank = rankData?.rank ?? null;
  const totalStudentsInClass = rankData?.totalStudents ?? (filteredExams.length > 0 ? 1 : 0);

  const prevExamRankData = prevExam ? list?.ranks?.[prevExam.name] : null;

  const rankTrend = useMemo(() => {
    if (!currentRank) return 'Section Standing';
    if (prevExamRankData && prevExamRankData.rank) {
      const diff = prevExamRankData.rank - currentRank;
      if (diff > 0) return `▲ Improved by ${diff}`;
      if (diff < 0) return `▼ Dropped by ${Math.abs(diff)}`;
      return '→ Maintained rank';
    }
    return `Class Standing`;
  }, [currentRank, prevExamRankData]);

  // Worded from the shared grade bands, so the line under the grade can never
  // disagree with the grade itself or with the parent panel's report.
  const gradeDescriptor = overallBand ? overallBand.label : 'Awaiting Results';

  // How many papers fell in each band — the same bands the grades are read off.
  const gradeCounts = useMemo(() => {
    const counts = new Map<string, number>(GRADE_BANDS.map((band) => [band.grade, 0]));
    filteredExams.forEach((m) => {
      if (m.isAbsent) return;
      const band = gradeForPaper(m.marksObtained, m.maxMarks, m.passMarks);
      if (band) counts.set(band.grade, (counts.get(band.grade) ?? 0) + 1);
    });
    return counts;
  }, [filteredExams]);

  // Dynamic Exam Dates
  const examDatesText = useMemo(() => {
    const dates = filteredExams
      .map((m) => m.examDate)
      .filter(Boolean)
      .sort();
    if (dates.length === 0) return '—';
    const minDate = dates[0];
    const maxDate = dates[dates.length - 1];
    if (minDate === maxDate) return formatDate(minDate);
    return `${formatDate(minDate)} – ${formatDate(maxDate)}`;
  }, [filteredExams]);

  /*
    When the school released this exam's results — the latest of its papers'
    publish times, since papers are released one by one. Not the last day the
    exam was sat: a paper sat in March can be published in April.
  */
  const publishedDateText = useMemo(() => {
    const published = filteredExams
      .map((m) => m.publishedAt)
      .filter((value): value is string => value !== null)
      .sort();
    return formatDate(published[published.length - 1]);
  }, [filteredExams]);

  // Dynamic Exam Status Badge
  const examStatus = useMemo(() => {
    if (filteredExams.length === 0) return { label: 'Scheduled', variant: 'scheduled' };
    const allMarked = filteredExams.every((m) => m.isAbsent || m.marksObtained !== null);
    if (allMarked) return { label: 'Completed', variant: 'completed' };
    const anyMarked = filteredExams.some((m) => !m.isAbsent && m.marksObtained !== null);
    if (anyMarked) return { label: 'In Progress', variant: 'in_progress' };
    return { label: 'Scheduled', variant: 'scheduled' };
  }, [filteredExams]);

  const classTeacher = card?.classTeacher ?? 'Not assigned yet';

  /**
   * The first paper of this exam a teacher wrote a remark on, or null.
   *
   * Only ever the teacher's own words. This used to fall back to a sentence
   * composed here from the percentage and printed under the class teacher's
   * name — a remark nobody wrote, attributed to a real person, and shown to the
   * child's family as theirs. It is signed by whoever entered that mark, which
   * is often a subject teacher and not the class teacher.
   */
  const remarkedPaper = useMemo(
    () => filteredExams.find((m) => m.remarks && m.remarks.trim().length > 0) ?? null,
    [filteredExams],
  );

  function downloadReportCard() {
    if (filteredExams.length === 0) {
      toast.error(
        'Nothing to put on a report card yet',
        'It can be printed once a teacher has marked a paper.',
      );
      return;
    }
    window.print();
  }

  return (
    <>
      <div className="flex flex-col gap-5 animate-fade-in font-sans print:hidden max-w-[1400px] mx-auto w-full pb-12">
        {/* 1. TOP TABS: Term Exams | Unit Tests | Assignments | Overall Report | Upcoming Exams */}
        <div className="flex items-center gap-4 sm:gap-6 border-b border-slate-200 dark:border-white/10 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {(['Term Exams', 'Unit Tests', 'Assignments', 'Overall Report', 'Upcoming Exams'] as const).map((tab) => {
            const isActive = activeCategoryTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={(e) => {
                  setActiveCategoryTab(tab);
                  e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                }}
                className={cn(
                  'pb-3 text-xs sm:text-[14px] font-bold transition-all cursor-pointer whitespace-nowrap relative shrink-0',
                  isActive
                    ? 'text-slate-900 dark:text-white font-extrabold'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200',
                )}
              >
                <span>
                  {tab}
                  {tab === 'Upcoming Exams' && upcoming.length > 0 && ` (${upcoming.length})`}
                </span>
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-[#4f46e5] rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {!isLoading && !error && <AwaitingResultsList papers={categoryAwaiting} whose="Your" />}

        {activeCategoryTab === 'Upcoming Exams' ? (
          <UpcomingExamsSchedule
            exams={upcoming}
            today={list?.today ?? null}
            isLoading={isLoading}
            error={error}
            onRetry={onRetry}
            emptyDescription={
              list && !list.section
                ? 'You have not been placed in a class yet. Once the school office does, your exams appear here.'
                : 'Nothing is on the exam timetable yet. Papers appear here as soon as your school publishes them.'
            }
          />
        ) : isCategoryUnmarked && categoryUpcoming.length > 0 ? (
          <UpcomingExamsSchedule
            exams={categoryUpcoming}
            today={list?.today ?? null}
            isLoading={isLoading}
            error={error}
            onRetry={onRetry}
            emptyDescription="Nothing of this kind is on the exam timetable yet."
          />
        ) : isCategoryUnmarked && categoryAwaiting.length > 0 ? null : isCategoryUnmarked ? (
          // "No unit tests yet" would contradict the sat papers listed just above.
          <div className="card">
            <EmptyState
              icon={<FileText className="h-5 w-5" />}
              title={`No ${activeCategoryTab.toLowerCase()} yet`}
              description="Papers of this kind appear here once your school puts them on the exam timetable, and your marks once a teacher has entered them."
            />
          </div>
        ) : (
          <>
          {/* 2. STAT CARDS ROW (4 Cards) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4">
            {/* Card 1: Overall Percentage */}
            <div className="rounded-2xl bg-amber-50/90 dark:bg-slate-900 border border-amber-200/90 dark:border-slate-800 px-5 py-3.5 shadow-xs hover:border-amber-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#d97706] text-white flex items-center justify-center shrink-0 shadow-xs">
                <Trophy className="h-6 w-6" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[14px] font-bold text-amber-800 dark:text-amber-400">Overall Percentage</span>
                <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 leading-tight mt-0.5">
                  {overallPercentage}
                </span>
                <span
                  className={cn(
                    'text-xs font-semibold mt-0.5 flex items-center gap-1',
                    percentageTrend.isPositive === true
                      ? 'text-emerald-700 dark:text-emerald-400'
                      : percentageTrend.isPositive === false
                        ? 'text-rose-600 dark:text-rose-400'
                        : 'text-slate-500 dark:text-slate-400',
                  )}
                >
                  {percentageTrend.text}
                </span>
              </div>
            </div>

            {/* Card 2: Total Subjects */}
            <div className="rounded-2xl bg-indigo-50/90 dark:bg-slate-900 border border-indigo-200/90 dark:border-slate-800 px-5 py-3.5 shadow-xs hover:border-indigo-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#4f46e5] text-white flex items-center justify-center shrink-0 shadow-xs">
                <FileText className="h-6 w-6" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[14px] font-bold text-indigo-800 dark:text-indigo-400">Total Subjects</span>
                <span className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400 leading-tight mt-0.5">
                  {filteredExams.length}
                </span>
                <span className="text-xs text-indigo-600/90 dark:text-slate-400 font-semibold mt-0.5 truncate">
                  {activeExamTitle}
                </span>
              </div>
            </div>

            {/* Card 3: Rank in Class */}
            <div className="rounded-2xl bg-emerald-50/90 dark:bg-slate-900 border border-emerald-200/90 dark:border-slate-800 px-5 py-3.5 shadow-xs hover:border-emerald-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#059669] text-white flex items-center justify-center shrink-0 shadow-xs">
                <BarChart3 className="h-6 w-6" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400">Rank in Class</span>
                <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-tight mt-0.5">
                  {currentRank ? `${currentRank} / ${totalStudentsInClass}` : '—'}
                </span>
                <span className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5 flex items-center gap-1">
                  {rankTrend}
                </span>
              </div>
            </div>

            {/* Card 4: Grade */}
            <div className="rounded-2xl bg-emerald-50/90 dark:bg-slate-900 border border-emerald-200/90 dark:border-slate-800 px-5 py-3.5 shadow-xs hover:border-emerald-300 dark:hover:border-slate-700 transition-all flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-[#059669] text-white flex items-center justify-center shrink-0 shadow-xs">
                <Star className="h-6 w-6 fill-current" />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400">Grade</span>
                <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-tight mt-0.5">
                  {overallBand ? overallBand.grade : '—'}
                </span>
                <span className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5 truncate">
                  {gradeDescriptor}
                </span>
              </div>
            </div>
          </div>

          {/* 3. MIDDLE SECTION: Table (Left) + Analytics (Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT: Scorecard Breakdown Table (8 cols) */}
            <div className="lg:col-span-8 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs flex flex-col overflow-hidden">
              {/* Table Header with Title, Dates, and Exam Selector */}
              <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2.5"> 
                    <h3 className="text-base capitalize sm:text-lg font-extrabold text-slate-700 dark:text-white">
                      {activeExamTitle}
                    </h3>
                    <span
                      className={cn(
                        'px-2.5 py-0.5 rounded-full text-xs font-bold border',
                        examStatus.variant === 'completed'
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800'
                          : examStatus.variant === 'in_progress'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200 border-amber-200 dark:border-amber-800'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border-blue-200 dark:border-blue-800',
                      )}
                    >
                      {examStatus.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 flex-wrap pt-0.5 font-medium">
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" /> {examDatesText}
                    </span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      <FileText className="h-3.5 w-3.5 text-slate-400" /> Published on {publishedDateText}
                    </span>
                  </div>
                </div>

                {/* Exam Term Selector Dropdown */}
                {examTermsList.length > 0 && (
                  <div className="relative shrink-0 w-full sm:w-auto sm:min-w-[260px]">
                    <select
                      value={activeExamName ?? ''}
                      onChange={(e) => setSelectedExamTest(e.target.value)}
                      className="w-full appearance-none rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 px-3.5 pr-9 py-2 text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100 focus:border-[#4f46e5] focus:outline-none cursor-pointer truncate"
                    >
                      {examTermsList.map((term) => (
                        <option key={term} value={term}>
                          {term}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                )}
              </div>

              {/* Scorecard Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm min-w-[620px]">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[14px] font-bold tracking-wide text-slate-700 dark:text-slate-300 align-middle">
                      <th className="py-3.5 px-4 w-12 text-center text-[14px] font-bold text-slate-700 dark:text-slate-300">#</th>
                      <th className="py-3.5 px-5 text-[14px] font-bold text-slate-700 dark:text-slate-300">Subject</th>
                      <th className="py-3.5 px-5 text-center text-[14px] font-bold text-slate-700 dark:text-slate-300">Max Marks</th>
                      <th className="py-3.5 px-5 text-center text-[14px] font-bold text-slate-700 dark:text-slate-300">Marks Obtained</th>
                      {showMarksPercentage && (
                        <th className="py-3.5 px-5 text-center text-[14px] font-bold text-slate-700 dark:text-slate-300">
                          Percentage
                        </th>
                      )}
                      <th className="py-3.5 px-5 text-center text-[14px] font-bold text-slate-700 dark:text-slate-300">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/80 dark:divide-slate-800/80 bg-white dark:bg-[#1b1d34]">
                    {isLoading && (
                      <tr>
                        <td colSpan={showMarksPercentage ? 6 : 5} className="p-6">
                          <SkeletonLines count={5} />
                        </td>
                      </tr>
                    )}

                    {!isLoading && error && (
                      <tr>
                        <td colSpan={showMarksPercentage ? 6 : 5} className="p-6">
                          <div className="flex flex-col items-start gap-3">
                            <Alert tone="critical" title="Could not load marks">
                              {error.message}
                            </Alert>
                            <Button variant="secondary" size="sm" onClick={onRetry}>
                              Try again
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {!isLoading && !error && filteredExams.length > 0 ? (
                      filteredExams.map((m, idx) => {
                        const pct =
                          m.isAbsent || m.marksObtained === null
                            ? null
                            : Math.round((m.marksObtained / m.maxMarks) * 100);
                        const band = m.isAbsent ? null : gradeForPaper(m.marksObtained, m.maxMarks, m.passMarks);
                        const grade = band ? band.grade : '—';
                        const theme = getSubjectTheme(m.subject.name);

                        return (
                          <tr
                            key={m.uuid}
                            className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors duration-150 ease-in-out align-middle"
                          >
                            <td className="py-3.5 px-4 text-center text-slate-400 dark:text-slate-500 font-mono text-[14px]">
                              {String(idx + 1).padStart(2, '0')}
                            </td>
                            <td className="py-3.5 px-5 font-bold">
                              <div className="flex items-center gap-3">
                                <span className={cn('p-1.5 rounded-lg shrink-0', theme.boxBg)}>
                                  {theme.icon}
                                </span>
                                <span className="text-slate-900 dark:text-white font-bold text-[14px]">
                                  {m.subject.name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3.5 px-5 text-center text-slate-700 dark:text-slate-200 font-mono font-medium text-[14px]">
                              {m.maxMarks}
                            </td>
                            <td className="py-3.5 px-5 text-center font-bold text-slate-900 dark:text-white font-mono text-[14px]">
                              {m.isAbsent ? 'AB' : (m.marksObtained ?? '—')}
                            </td>
                            {showMarksPercentage && (
                              <td className="py-3.5 px-5 text-center text-slate-700 dark:text-slate-200 font-mono font-medium text-[14px]">
                                {pct === null ? '—' : `${pct}%`}
                              </td>
                            )}
                            <td className="py-3.5 px-5 text-center">
                              <span
                                className={cn(
                                  'inline-block px-2.5 py-0.5 rounded-full font-bold text-xs text-center',
                                  gradeBadgeClass(band),
                                )}
                              >
                                {grade}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      !isLoading &&
                      !error && (
                        <tr>
                          <td colSpan={showMarksPercentage ? 6 : 5} className="py-12 text-center text-slate-400 dark:text-slate-500 italic text-sm">
                            {marksList.length === 0
                              ? 'No papers have been marked yet.'
                              : `No marks records found for "${activeExamName}".`}
                          </td>
                        </tr>
                      )
                    )}

                    {/* Total / Aggregate Summary Row */}
                    {!isLoading && filteredExams.length > 0 && (
                      <tr className="bg-slate-100/80 dark:bg-slate-800/80 font-black border-t-2 border-slate-200 dark:border-slate-700 align-middle">
                        <td className="py-3.5 px-4 text-center" />
                        <td className="py-3.5 px-5 text-slate-900 dark:text-white text-base tracking-wider font-extrabold">
                          Total
                        </td>
                        <td className="py-3.5 px-5 text-center text-slate-900 dark:text-white font-mono text-[14px] font-bold">
                          {totalMax}
                        </td>
                        <td className="py-3.5 px-5 text-center text-slate-900 dark:text-white font-mono text-[14px] font-bold">
                          {totalObtainedText}
                        </td>
                        {showMarksPercentage && (
                          <td className="py-3.5 px-5 text-center text-slate-900 dark:text-white font-mono text-[14px] font-bold">
                            {overallPercentage}
                          </td>
                        )}
                        <td className="py-3.5 px-5 text-center">
                          <span
                            className={cn(
                              'inline-block px-2.5 py-0.5 rounded-full font-bold text-xs text-center',
                              gradeBadgeClass(overallBand),
                            )}
                          >
                            {overallBand?.grade ?? '—'}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* RIGHT: Subject-wise Performance & Grade Distribution (4 cols) */}
            <div className="lg:col-span-4 flex flex-col gap-5 sm:gap-6">
              {/* Subject-wise Performance Card */}
              <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-5 flex flex-col gap-4">
                <div className="flex items-center gap-2.5 border-b border-slate-100 dark:border-white/10 pb-3">
                  <BarChart3 className="h-5 w-5 text-[#4f46e5] dark:text-indigo-400" />
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                    Subject-wise Performance
                  </h4>
                </div>

                <div className="flex flex-col gap-3 pt-1">
                  {!isLoading && filteredExams.length === 0 && (
                    <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
                      {marksList.length === 0
                        ? 'Your subject-wise performance appears here once a teacher has marked a paper.'
                        : `No marks recorded for "${activeExamName}" yet.`}
                    </p>
                  )}
                  {filteredExams.map((m) => {
                    const pct =
                      m.isAbsent || m.marksObtained === null
                        ? null
                        : Math.round((m.marksObtained / m.maxMarks) * 100);
                    const theme = getSubjectTheme(m.subject.name);

                    return (
                      <div key={m.uuid} className="flex items-center justify-between gap-3 text-xs sm:text-sm">
                        <span className="flex-1 min-w-0 truncate font-medium text-slate-700 dark:text-slate-300" title={m.subject.name}>
                          {m.subject.name}
                        </span>
                        <div className="w-24 sm:w-32 shrink-0 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          {/* No fill at all without a score: an empty track beside "AB" or "—", not a bar at zero. */}
                          {pct !== null && (
                            <div
                              className={cn('h-full rounded-full transition-all duration-500', theme.barColor)}
                              style={{ width: `${pct}%` }}
                            />
                          )}
                        </div>
                        <span className="w-9 text-right font-bold text-slate-800 dark:text-white font-mono shrink-0">
                          {m.isAbsent ? 'AB' : pct === null ? '—' : `${pct}%`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grade Distribution Card */}
              <div className="rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-5 flex flex-col gap-4">
                <div className="flex items-center gap-2.5 border-b border-slate-100 dark:border-white/10 pb-3">
                  <BarChart3 className="h-5 w-5 text-[#4f46e5] dark:text-indigo-400" />
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                    Grade Distribution
                  </h4>
                </div>

                <div className="flex items-center justify-between gap-4 pt-1">
                  {/* SVG Donut Chart */}
                  <div className="relative h-24 w-24 shrink-0 flex items-center justify-center">
                    <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
                      <path
                        className="text-slate-100 dark:text-slate-800"
                        strokeWidth="4"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-[#4f46e5]"
                        strokeDasharray={`${currentPctNum ?? 0}, 100`}
                        strokeWidth="4"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                      <span className="text-sm sm:text-base font-black text-slate-900 dark:text-white leading-none">
                        {overallPercentage}
                      </span>
                      <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold mt-0.5">
                        Overall
                      </span>
                    </div>
                  </div>

                  {/* Grade Counts List */}
                  <div className="flex flex-col gap-1.5 text-xs flex-1 min-w-0">
                    {GRADE_BANDS.map((band) => (
                      <div key={band.grade} className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 font-semibold text-slate-600 text-sm dark:text-slate-300">
                          <span className={cn('h-2 w-2 rounded-full', GRADE_TONE_CLASSES[band.tone].dot)} /> {band.grade}
                        </span>
                        <span className="font-mono font-bold text-slate-800 dark:text-slate-200">
                          {gradeCounts.get(band.grade) ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. BOTTOM ROW: Teacher's Remarks (Left) + Download Report (Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Teacher's Remarks */}
            <div className="lg:col-span-8 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4.5 w-4.5 text-[#4f46e5] dark:text-indigo-400" />
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Teacher's Remarks</h4>
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-500 font-semibold">
                  Published on {publishedDateText}
                </span>
              </div>

              <div className="p-4 rounded-xl bg-slate-50/90 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Signed only when the server knows who entered the mark — never guessed. */}
                {remarkedPaper?.gradedBy && (
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      aria-hidden
                      className="h-11 w-11 rounded-full bg-indigo-50 text-[#4f46e5] dark:bg-indigo-950/60 dark:text-indigo-400 flex items-center justify-center shrink-0"
                    >
                      <UserRound className="h-5 w-5" />
                    </span>
                    <div className="flex flex-col">
                      <span className="text-xs sm:text-[14px] font-bold text-slate-900 dark:text-white">
                        {remarkedPaper.gradedBy}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                        {remarkedPaper.subject.name}
                      </span>
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    'flex-1 flex items-start gap-2',
                    remarkedPaper?.gradedBy && 'sm:border-l border-slate-200 dark:border-slate-700 sm:pl-4',
                  )}
                >
                  <Quote className="h-4 w-4 text-[#4f46e5] shrink-0 rotate-180 mt-0.5" />
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">
                    {remarkedPaper?.remarks ?? 'No remarks from the teacher on this exam.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Download Report Card Action */}
            <div className="lg:col-span-4 rounded-2xl bg-white dark:bg-[#1b1d34] border border-slate-200/80 dark:border-white/10 shadow-xs p-5 flex flex-col justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-xl bg-indigo-50 text-[#4f46e5] dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/80 dark:border-indigo-800 shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">Download Report</h4>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                    {activeExamName
                      ? `Download the detailed report card for ${activeExamName}.`
                      : 'Your report card can be downloaded once a teacher has marked a paper.'}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={downloadReportCard}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[#4f46e5] hover:bg-[#4338ca] text-white font-bold text-xs sm:text-sm shadow-xs transition-all cursor-pointer mt-auto"
              >
                <Download className="h-4 w-4" />
                <span>Download Report (PDF)</span>
              </button>
            </div>
          </div>
          </>
        )}
      </div>


      {/*
        THE REPORT CARD, FOR PAPER ONLY (PRINTING)
      */}
      <div className="hidden print:block text-black">
        <div className="border-b-2 border-black pb-3 text-center">
          <h1 className="text-xl font-bold">{card?.instituteName || 'Report card'}</h1>
          <p className="mt-1 text-sm">
            Report card{activeExamName ? ` · ${activeExamName}` : ''}
            {card?.academicYearName ? ` · ${card.academicYearName}` : ''}
          </p>
        </div>

        <table className="mt-4 w-full text-sm">
          <tbody>
            <tr>
              <td className="w-32 py-1 font-semibold">Name</td>
              <td className="py-1">{card?.fullName ?? ''}</td>
              <td className="w-32 py-1 font-semibold">Admission no.</td>
              <td className="py-1">{card?.admissionNumber ?? ''}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Class</td>
              <td className="py-1">{card?.classDisplayName ?? ''}</td>
              <td className="py-1 font-semibold">Roll no.</td>
              <td className="py-1">{card?.rollNumber ?? ''}</td>
            </tr>
            <tr>
              <td className="py-1 font-semibold">Class teacher</td>
              <td className="py-1" colSpan={3}>
                {classTeacher}
              </td>
            </tr>
          </tbody>
        </table>

        <table className="mt-5 w-full border-collapse text-sm">
          <thead>
            <tr className="border-y border-black text-left">
              <th className="py-2">Subject</th>
              <th className="py-2 text-right">Marks</th>
              <th className="py-2 text-right">Out of</th>
              <th className="py-2 text-right">Pass mark</th>
              <th className="py-2 text-right">Grade</th>
              <th className="py-2">Remarks</th>
            </tr>
          </thead>
          <tbody>
            {filteredExams.map((m) => {
              const band = m.isAbsent ? null : gradeForPaper(m.marksObtained, m.maxMarks, m.passMarks);

              return (
                <tr key={m.uuid} className="border-b border-black/20">
                  <td className="py-1.5">{m.subject.name}</td>
                  <td className="py-1.5 text-right">{m.isAbsent ? 'AB' : (m.marksObtained ?? '—')}</td>
                  <td className="py-1.5 text-right">{m.maxMarks}</td>
                  <td className="py-1.5 text-right">{m.passMarks}</td>
                  <td className="py-1.5 text-right">{band ? band.grade : '—'}</td>
                  <td className="py-1.5">
                    {m.remarks ?? ''}
                    {m.remarks && m.gradedBy ? ` — ${m.gradedBy}` : ''}
                  </td>
                </tr>
              );
            })}
            <tr className="border-b-2 border-black font-semibold">
              <td className="py-2">Total</td>
              <td className="py-2 text-right">{totalObtainedText}</td>
              <td className="py-2 text-right">{totalMax}</td>
              <td className="py-2 text-right" />
              <td className="py-2 text-right">{overallBand ? overallBand.grade : '—'}</td>
              <td className="py-2">{overallPercentage}</td>
            </tr>
          </tbody>
        </table>

        <p className="mt-3 text-2xs">
          Papers the child was absent for are shown as AB and are left out of the total.
        </p>

        <div className="mt-12 flex items-end justify-between text-sm">
          <span className="border-t border-black pt-1">Class teacher</span>
          <span className="border-t border-black pt-1">Principal</span>
          <span className="border-t border-black pt-1">Parent</span>
        </div>
      </div>
    </>
  );
}
